<template>
  <v-row v-if="mode === 'edit'">
    <!-- When not editing, show Add/Edit Summary -->
    <v-col cols="12" v-if="!state.isEdit" class="d-flex justify-end">
      <v-btn
        color="primary"
        variant="flat"
        class="tw:ml-2!"
        :loading="props.loading"
        :disabled="props.disabled"
        @click="startEdit"
      >
        {{ _.isEmpty(state.summary.content) ? "Add Summary" : "Edit Summary" }}
      </v-btn>
    </v-col>

    <!-- When editing, show Save + Cancel and Regenerate buttons -->
    <v-col v-else cols="12" class="d-flex justify-space-between">
      <!-- Prompt selector and Regenerate button -->
      <div class="d-flex align-center">
        <v-select
          density="compact"
          class="tw:ml-2! prompt-selector"
          item-title="title"
          item-value="id"
          variant="solo-filled"
          v-model="selectedPrompt"
          v-if="state.isEdit"
          hide-details
          :items="prompts"
          :loading="state.isLoading || props.loading"
          :disabled="state.isLoading || props.loading"
        />
        <v-btn
          color="primary"
          variant="flat"
          @click="reGenerate"
          class="tw:ml-2!"
          :disabled="state.isLoading || props.loading || props.disabled"
          v-if="state.isEdit"
          >{{ _.isEmpty(state.html) ? "Generate" : "Regenerate" }}</v-btn
        >
      </div>

      <!-- Save + Cancel buttons -->
      <div>
        <v-btn
          color="primary"
          variant="flat"
          class="tw:ml-2!"
          :disabled="state.isLoading || props.loading || props.disabled"
          @click="save"
        >
          Save
        </v-btn>
        <v-btn
          color="error"
          variant="flat"
          class="tw:ml-2!"
          :disabled="state.isLoading || props.loading || props.disabled"
          @click="cancel"
        >
          Cancel
        </v-btn>
      </div>
    </v-col>
  </v-row>

  <div class="tw:mt-2!">
    <Editor
      v-model="state.html"
      v-if="state.isEdit && state.mode === 'edit'"
      class="tw:w-full mb-6"
      :style="{ height: state.html === '' ? '500px' : 'auto' }"
      :readonly="state.isLoading || props.loading"
    />
    <VMarkdownView mode="light" :content="toMarkdown(state.summary).content" v-else />
  </div>
</template>

<script setup lang="ts">
import { htmlToMarkdown, markdownToHtml } from "@/utils";
import _ from "lodash";
import { reactive, ref, watch } from "vue";
// @ts-ignore
import { VMarkdownView } from "vue3-markdown";
import "vue3-markdown/dist/vue3-markdown.css";
import Editor from "primevue/editor";
import usePrompt from "@/hooks/usePrompt";
import { useReportStore } from "@/stores/report";
import Profile from "@/actions/Profile";

const props = defineProps<{
  summary?: string | null;
  save?: (value: string, promptId: string | null) => Promise<void>;
  regenerate?: (value: string, promptId: string | null) => Promise<string | false>;
  prompt: string;
  mode?: "edit" | "view";
  loading: boolean;
  disabled: boolean;
  promptId?: string | null;
}>();

interface Summary {
  type: "html" | "markdown";
  content: string;
}

const startEdit = async () => {
  state.isEdit = true;
  // seed the editor with the last‐saved markdown
  state.html = (await toHtml(state.summary)).content;
};

const { prompts, findPromptContent } = usePrompt();
const reportStore = useReportStore();

const selectedPrompt = ref<string | null>(null);
const lastPrompt = ref<string | null>(null);

const state = reactive({
  isLoading: false,
  summary: { type: "html", content: "" } as Summary,
  previousSummary: { type: "html", content: "" } as Summary,
  isPreviousSummarySet: false,
  mode: props.mode ?? "view",
  isEdit: false,
  isInitialSummaryGenerated: false,
  shouldGenerateSummary: false,
  timeoutId: null as null | number,
  html: "" as string
});

const toHtml = async (summary: Summary): Promise<Summary> => {
  if (summary.type === "html") {
    return summary;
  } else {
    return {
      type: "html",
      content: await markdownToHtml(summary.content)
    };
  }
};

const toMarkdown = (summary: Summary): Summary => {
  if (summary.type === "markdown") {
    return summary;
  } else {
    return {
      type: "markdown",
      content: htmlToMarkdown(summary.content)
    };
  }
};

const save = async () => {
  state.isEdit = false;
  if (state.isLoading) return;
  state.summary = {
    content: htmlToMarkdown(state.html),
    type: "markdown"
  };
  if (state.summary.content === state.previousSummary.content) return;
  if (props.save) {
    await props.save(state.summary.content, selectedPrompt.value ?? null);
    state.previousSummary = state.summary;
  }
  lastPrompt.value = selectedPrompt.value;
};

const reset = async () => {
  state.html = (await toHtml(state.summary)).content;
};

const cancel = async () => {
  // revert the editor’s HTML back to the last saved summary
  state.html = (await toHtml(state.summary)).content;
  // exit edit‐mode without calling save()
  state.isEdit = false;
};

const reGenerate = async () => {
  if (state.isLoading || !props?.regenerate) return;
  state.isLoading = true;
  const prompt =
    selectedPrompt.value === null || selectedPrompt.value === "default"
      ? reportStore.getPrompt()
      : ((await Profile.getPrompt(selectedPrompt.value!)).data?.prompt.content ??
        reportStore.getPrompt());
  const response = await props?.regenerate(
    `${prompt} ${props.prompt}`,
    selectedPrompt.value ?? null
  );
  if (response !== false) {
    // Commented-out to treat regenerate as a preview operation
    // state.summary = {
    //   content: response,
    //   type: "markdown"
    // };
    // only update the editor content, don’t touch state.summary
    state.html = await markdownToHtml(response);
  }
  // Commented-out to treat regenerate as a preview operation
  // state.isEdit = false;
  // if (response !== false) {
  //   await save();
  // }
  state.isLoading = false;
  lastPrompt.value = selectedPrompt.value;
};

watch(
  [state, props],
  ([newState, newProps], [oldState, oldProps]) => {
    if (newProps.loading === false && newState.isLoading === false) {
      if (newState.shouldGenerateSummary === true) {
        const onLoadingFinishHandler = async () => {
          state.shouldGenerateSummary = false;
          await reGenerate();
          await save();
          state.isInitialSummaryGenerated = true;
        };
        onLoadingFinishHandler();
      }
    }
  },
  {
    deep: true,
    immediate: true
  }
);

watch(
  prompts,
  (value) => {
    selectedPrompt.value = _.head(value)?.id ?? null;
  },
  { immediate: true }
);

// When we enter edit mode, if we’ve already picked a prompt for this session,
// preserve it; otherwise fall back to default or initial prop.
watch(
  () => state.isEdit,
  (isEdit) => {
    if (!isEdit) return;

    // if they already chose something in this session, keep it
    if (lastPrompt.value) {
      selectedPrompt.value = lastPrompt.value;
      return;
    }

    // otherwise, if props.promptId was provided, use it
    if (props.promptId) {
      lastPrompt.value = props.promptId;
      selectedPrompt.value = props.promptId;
      return;
    }

    // else pick your “default” prompt
    const sys = prompts.value.find((p) => p.id === "default");
    lastPrompt.value = sys?.id ?? null;
    selectedPrompt.value = lastPrompt.value;
  }
);

// Initialize from props.promptId or system default once, then don’t override again:
watch(
  () => props.promptId,
  (newId, oldId) => {
    // only on first set, or when saving to server really happens:
    if (newId != null && lastPrompt.value == null) {
      lastPrompt.value = newId;
      selectedPrompt.value = newId;
    }
  },
  { immediate: true }
);

watch(
  () => state.summary,
  async (newValue) => {
    state.html = (await toHtml(newValue)).content;
  }
);

watch(
  () => props.summary,
  async (newValue) => {
    if (newValue !== null && newValue !== undefined) {
      state.summary = {
        content: newValue,
        type: "markdown"
      };
      if (state.isPreviousSummarySet === false) {
        state.previousSummary = {
          content: newValue,
          type: "markdown"
        };
        state.isPreviousSummarySet = true;
      }
    }
  },
  { deep: true }
);
</script>

<style>
.markdown-body[data-theme="light"] h2 {
  font-size: 1rem !important;
}
.markdown-body[data-theme="light"] h3 {
  font-size: 0.95rem !important;
}
.markdown-body {
  font-size: 0.9rem !important;
  background: transparent !important;
}
.prompt-selector {
  width: 200px !important;
  max-width: 200px !important;
  max-height: 36px;
}
.prompt-selector .v-input__control {
  width: 100% !important;
  height: 100% !important;
}
</style>
